import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as microsoftTeams from '@microsoft/teams-js';
import { 
  SearchBox,
  Spinner,
  Text,
  Body1,
  Caption1
} from '@fluentui/react-components';
import { FileItem } from './FileItem';
import { DrawioViewer } from './DrawioViewer';
import { getDrawioFiles, getFileMetadata, getChannelDisplayName } from '../services/graphApi';

export interface DrawioFile {
  id: string;
  name: string;
  webUrl: string;
  downloadUrl: string;
  lastModifiedDateTime: string;
  createdDateTime: string;
  size: number;
  path?: string;
  /** Drive ID from parentReference – used for the direct draw.io link */
  driveId?: string;
}

const LAST_OPENED_KEY = 'teams-drawio-lastOpened';

function getLastOpened(): { teamId: string; fileId: string } | null {
  try {
    const raw = localStorage.getItem(LAST_OPENED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.teamId && parsed?.fileId) return parsed;
    return null;
  } catch {
    return null;
  }
}

function setLastOpened(teamId: string, fileId: string) {
  try {
    localStorage.setItem(LAST_OPENED_KEY, JSON.stringify({ teamId, fileId }));
  } catch {
    // localStorage may be disabled
  }
}

function clearLastOpened() {
  try {
    localStorage.removeItem(LAST_OPENED_KEY);
  } catch {
    // localStorage may be disabled
  }
}

/** Derives the channel name from the file path (first or second segment if "Shared Documents"/"Dokumente" is prefixed). */
function getChannelFromPath(path: string | undefined): string | null {
  if (!path || !path.trim()) return null;
  const segments = path.split(' > ').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return null;
  const docLibNames = ['Shared Documents', 'Dokumente', 'Allgemeine Dokumente', 'Documents partagés', 'Documentos compartidos'];
  const channelSegment = docLibNames.includes(segments[0]) && segments.length > 1 ? segments[1] : segments[0];
  return channelSegment || null;
}

/**
 * Known translations for the default "General" channel across languages.
 * SharePoint folder names may use the original language while Teams displays
 * the localised channel name – this mapping bridges that gap.
 */
const GENERAL_CHANNEL_ALIASES: string[] = [
  'General',
  'Allgemein',
  'Général',
  'Generale',
  'Geral',
  'Algemeen',
  'Generelt',          // Danish / Norwegian
  'Allmänt',           // Swedish
  'Yleinen',           // Finnish
  'Ogólny',            // Polish
  'Obecné',            // Czech
  'Általános',         // Hungarian
  'Genel',             // Turkish
  'Общий',             // Russian
  'Загальне',          // Ukrainian
  '全般',              // Japanese
  '일반',              // Korean
  '常规',              // Chinese (Simplified)
  '一般',              // Chinese (Traditional)
];

/**
 * Normalises a channel name for comparison.
 * Returns the canonical English "General" for any known alias,
 * otherwise returns the lowercased name.
 */
function normaliseChannelName(name: string): string {
  if (GENERAL_CHANNEL_ALIASES.some(alias => alias.localeCompare(name, undefined, { sensitivity: 'base' }) === 0)) {
    return 'general';
  }
  return name.toLowerCase();
}

/** Checks whether a file belongs to the given channel. */
function fileBelongsToChannel(file: DrawioFile, channelDisplayName: string): boolean {
  const fileChannel = getChannelFromPath(file.path);
  if (!fileChannel) return false;
  // Exact match first (fast path)
  if (fileChannel === channelDisplayName) return true;
  // Normalised match (handles "General" ↔ "Allgemein" etc.)
  return normaliseChannelName(fileChannel) === normaliseChannelName(channelDisplayName);
}

/**
 * Checks whether a URL parameter value is a real value or an unresolved Teams placeholder.
 * Teams Mobile may leave placeholders like "{channelName}" or "{teamName}" as literal strings
 * if they are not officially supported placeholders.
 */
function isUnresolvedPlaceholder(value: string | null | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  // Detect literal "{placeholder}" patterns (unresolved Teams placeholders)
  return /^\{[a-zA-Z]+\}$/.test(trimmed) || trimmed === '';
}

/**
 * Reads team/channel context from URL parameters (fallback for Mobile).
 * Teams injects {groupId} (officially supported) into the contentUrl when the tab loads.
 * Custom placeholders like {channelName} / {teamName} may NOT be resolved on Mobile —
 * in that case we treat them as absent.
 */
function getContextFromUrl(): { teamId: string; channelName: string | null; channelId: string | null; teamName: string | null } | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const rawGroupId = params.get('groupId')?.trim();
    if (!rawGroupId || isUnresolvedPlaceholder(rawGroupId)) return null;

    const rawChannelName = params.get('channelName')?.trim() || null;
    const rawChannelId = params.get('channelId')?.trim() || null;
    const rawTeamName = params.get('teamName')?.trim() || null;

    return {
      teamId: rawGroupId,
      channelName: isUnresolvedPlaceholder(rawChannelName) ? null : rawChannelName,
      channelId: isUnresolvedPlaceholder(rawChannelId) ? null : rawChannelId,
      teamName: isUnresolvedPlaceholder(rawTeamName) ? null : rawTeamName,
    };
  } catch {
    return null;
  }
}

export const FileList: React.FC = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<DrawioFile[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<DrawioFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [teamName, setTeamName] = useState('');
  const [channelName, setChannelName] = useState<string | null>(null);
  const [teamId, setTeamId] = useState('');
  const [selectedFile, setSelectedFile] = useState<DrawioFile | null>(null);

  const initializeApp = useCallback(async () => {
    try {
      console.log('[DEBUG] Initializing Teams SDK...');
      // Initialize Teams SDK
      await microsoftTeams.app.initialize();
      const context = await microsoftTeams.app.getContext();
      
      console.log('[DEBUG] Teams context received:', {
        teamId: context.team?.groupId,
        teamName: context.team?.displayName,
        channelId: context.channel?.id,
        channelName: context.channel?.displayName,
        userPrincipalName: context.user?.userPrincipalName,
        tenantId: context.user?.tenant?.id,
        frameContext: context.page?.frameContext
      });

      // Fallback for Mobile: getContext() often returns empty values there.
      // Teams injects groupId/channelId via URL placeholders (officially supported).
      // channelName/teamName are custom placeholders and may NOT be resolved on Mobile.
      const urlContext = getContextFromUrl();
      console.log('[DEBUG] URL context:', urlContext);

      const ctxTeamId = context.team?.groupId || urlContext?.teamId || '';
      const ctxTeamName = context.team?.displayName || urlContext?.teamName || 'Team';
      
      // Channel name resolution strategy:
      // 1. SDK context (best – works on Desktop/Browser)
      // 2. URL parameter channelName (works if Teams resolves the placeholder)
      // 3. Resolve channelId → channelName via Graph API (reliable Mobile fallback)
      // 4. null → show all files (graceful degradation, better than empty list)
      let ctxChannelName = context.channel?.displayName ?? urlContext?.channelName ?? null;
      const ctxChannelId = context.channel?.id || urlContext?.channelId || null;

      if (!ctxChannelName && ctxChannelId && ctxTeamId) {
        console.log('[DEBUG] Channel-Name nicht verfügbar, löse über Graph API auf (channelId:', ctxChannelId, ')');
        try {
          ctxChannelName = await getChannelDisplayName(ctxTeamId, ctxChannelId);
          console.log('[DEBUG] Channel-Name über Graph API aufgelöst:', ctxChannelName);
        } catch (resolveErr) {
          console.warn('[DEBUG] Channel-Name konnte nicht aufgelöst werden:', resolveErr);
        }
      }

      if (!ctxChannelName) {
        console.log('[DEBUG] Kein Channel-Kontext verfügbar – zeige alle Dateien des Teams');
      }

      if (urlContext && !context.team?.groupId) {
        console.log('[DEBUG] Mobile-Fallback: Kontext aus URL-Parametern', urlContext);
      }

      setTeamName(ctxTeamName);
      setChannelName(ctxChannelName);
      setTeamId(ctxTeamId);

      // Effective context for loadFiles (SDK + URL fallback merged)
      const effectiveContext = {
        ...context,
        team: { ...context.team, groupId: ctxTeamId, displayName: ctxTeamName },
        channel: { ...context.channel, displayName: ctxChannelName },
      };

      // Load files
      const loadedFiles = await loadFiles(effectiveContext);

      // Hash route #view/teamId/fileId ("Open in browser") – open file directly in viewer
      const hashMatch = window.location.hash.match(/^#view\/([^/]+)\/([^/]+)$/);
      if (hashMatch) {
        const viewTeamId = decodeURIComponent(hashMatch[1]);
        const viewFileId = decodeURIComponent(hashMatch[2]);
        setTeamId(viewTeamId);
        try {
          const fileMeta = await getFileMetadata(viewTeamId, viewFileId);
          setSelectedFile(fileMeta);
        } catch (hashErr: any) {
          console.error('[FileList] Fehler beim Laden der Hash-Route:', hashErr);
          setError(hashErr?.message || t('fileList.fileLoadError'));
        }
      } else {
        // No hash link: auto-open last opened diagram
        const last = getLastOpened();
        if (last && last.teamId === ctxTeamId && loadedFiles.length > 0) {
          const found = loadedFiles.find((f) => f.id === last.fileId);
          if (found) setSelectedFile(found);
        }
      }
    } catch (error: any) {
      console.error('[ERROR] Fehler beim Initialisieren:', error);
      setError(t('fileList.initError', { message: error?.message || String(error) }));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  useEffect(() => {
    // Filter on search
    const filtered = files.filter(f => 
      f.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredFiles(filtered);
  }, [searchTerm, files]);

  // Remember last opened diagram (for auto-open on next start)
  useEffect(() => {
    if (selectedFile && teamId) {
      setLastOpened(teamId, selectedFile.id);
    }
  }, [selectedFile, teamId]);

  const loadFiles = async (context: any): Promise<DrawioFile[]> => {
    try {
      const teamId = context.team?.groupId;
      const channelDisplayName = context.channel?.displayName;
      
      if (!teamId) {
        setError(t('fileList.noTeamContext'));
        setLoading(false);
        return [];
      }

      // Graph API call for files (loads all .drawio files of the entire team)
      const allDrawioFiles = await getDrawioFiles(teamId);
      
      console.log('[DEBUG] All drawio files from API:', allDrawioFiles.length, allDrawioFiles.map(f => ({ name: f.name, path: f.path })));
      console.log('[DEBUG] Channel filter:', channelDisplayName ?? '(none – showing all)');

      // Filter by current channel if channel context is present (tab in a channel)
      let drawioFiles: DrawioFile[];
      if (channelDisplayName) {
        drawioFiles = allDrawioFiles.filter(f => fileBelongsToChannel(f, channelDisplayName));
        console.log('[DEBUG] Files after channel filter:', drawioFiles.length);
        if (drawioFiles.length === 0 && allDrawioFiles.length > 0) {
          // Channel filter removed all files – the channel name may not match any path.
          // Log details to help diagnose, then fall back to showing all files.
          console.warn(
            '[DEBUG] Channel filter removed all files! Channel:', channelDisplayName,
            '| File paths:', allDrawioFiles.map(f => f.path),
            '→ Falling back to showing all files.'
          );
          drawioFiles = allDrawioFiles;
        }
      } else {
        drawioFiles = allDrawioFiles;
      }
      
      setFiles(drawioFiles);
      setFilteredFiles(drawioFiles);
      return drawioFiles;
    } catch (error: any) {
      console.error('Fehler beim Laden der Dateien:', error);
      setError(error.message || t('fileList.loadFilesError'));
      return [];
    } finally {
      setLoading(false);
    }
  };

  // When a file is selected, show the DrawioViewer
  if (selectedFile && teamId) {
    return (
      <DrawioViewer
        file={selectedFile}
        teamId={teamId}
        onBack={() => {
          clearLastOpened();
          setSelectedFile(null);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner size="large" label={t('fileList.loading')} />
      </div>
    );
  }

  if (error) {
    const isAuthError = error.includes('autorisiert') || error.includes('Authentifizierung') ||
      error.includes('authorized') || error.includes('authentication');
    
    return (
      <div className="error-container">
        <Text size={500} weight="semibold">⚠️ {t('fileList.errorTitle')}</Text>
        <Body1 style={{ marginTop: '12px', marginBottom: '12px' }}>{error}</Body1>
        
        {isAuthError && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#FFF4CE', borderRadius: '4px' }}>
            <Text size={400} weight="semibold">🔧 {t('fileList.authSuggestionsTitle')}</Text>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>{t('fileList.authSuggestionConsole')}</li>
              <li>{t('fileList.authSuggestionAzure')}
                <ul style={{ marginTop: '4px' }}>
                  <li>{t('fileList.authSuggestionPerms')}</li>
                  <li>{t('fileList.authSuggestionConsent')}</li>
                  <li>{t('fileList.authSuggestionClientId')}</li>
                </ul>
              </li>
              <li>{t('fileList.authSuggestionManifest')}</li>
              <li>{t('fileList.authSuggestionDocsPrefix')}<a href="#" onClick={(e) => { e.preventDefault(); console.log('Docs:', 'AZURE_AD_SETUP.md'); }}>AZURE_AD_SETUP.md</a>{t('fileList.authSuggestionDocsSuffix')}</li>
            </ul>
          </div>
        )}
        
        <Caption1 style={{ marginTop: '16px' }}>{t('fileList.errorConsole')}</Caption1>
      </div>
    );
  }

  const channelPart = channelName ? t('fileList.channelSeparator', { channelName }) : '';
  return (
    <div className="file-list-container">
      <header className="app-header">
        <Text size={700} weight="semibold" as="h1">
          {t('fileList.header', { teamName, channel: channelPart })}
        </Text>
      </header>
      
      <div style={{ marginBottom: '16px' }}>
        <Body1 style={{ color: '#616161' }}>
          {t('fileList.filesFound', { count: filteredFiles.length })}
        </Body1>
      </div>

      <div className="search-box-wrapper">
        <SearchBox
          placeholder={t('fileList.searchPlaceholder')}
          value={searchTerm}
          onChange={(_, data) => setSearchTerm(data?.value || '')}
          className="search-box"
        />
      </div>

      <div className="file-list">
        {filteredFiles.length === 0 ? (
          <div className="no-files">
            {searchTerm ? (
              <>
                <Text size={400}>{t('fileList.noResults', { term: searchTerm })}</Text>
                <Caption1>{t('fileList.tryOtherSearch')}</Caption1>
              </>
            ) : (
              <>
                <Text size={400}>{t('fileList.noFiles')}</Text>
                <Caption1>{t('fileList.noFilesHint')}</Caption1>
              </>
            )}
          </div>
        ) : (
          filteredFiles.map(file => (
            <FileItem key={file.id} file={file} teamId={teamId} onOpen={() => setSelectedFile(file)} />
          ))
        )}
      </div>
    </div>
  );
};
